import { calcPeriod } from './engine.js';
import { daysFor, expectedWork, periodInfo } from './period.js';
import type {
  CompanySettings,
  DayRow,
  EditLogEntry,
  Employee,
  Timesheet,
  TimesheetStatus,
  TrailEvent,
} from './types.js';

/**
 * A work report for one employee over one period.
 *
 * The hours already exist — `calcPeriod` walks the days and returns them. What
 * was missing was anywhere to *see* them: an employee could sign a timesheet
 * without being shown the sheet, and an administrator could approve one the
 * same way. A signature over a figure nobody displayed is not a signature over
 * anything.
 *
 * So this assembles the sheet as a document: every day of the period in order,
 * including the empty ones, with what was worked, how it was classified, and
 * whether anybody has edited it since. Days with nothing on them are kept
 * rather than filtered — a gap in the middle of a month is information, and a
 * list that silently omits it invites the reader to assume it was worked.
 */

export interface TimesheetDay {
  date: string;
  /** 0 = Sunday, as `Date.getDay()` reports it. */
  dow: number;
  weekend: boolean;
  holiday: boolean;
  /** Hours as recorded. Zero for absence and for days nobody worked. */
  hours: number;
  dvH: number;
  yv1H: number;
  yv2H: number;
  gross: number;
  sick: boolean;
  vacation: boolean;
  /** The hours on this day have been corrected since they were first recorded. */
  edited: boolean;
  /** Nothing recorded at all — distinct from a recorded zero. */
  empty: boolean;
}

export interface TimesheetTotals {
  dvH: number;
  yv1H: number;
  yv2H: number;
  hours: number;
  /** Days with something on them: worked, sick or on holiday. */
  daysRecorded: number;
  daysWorked: number;
  sickDays: number;
  vacationDays: number;
  gross: number;
}

export interface TimesheetDoc {
  empId: string;
  name: string;
  kennitala: string;
  jobTitle: string;
  periodStart: string;
  periodEnd: string;
  days: TimesheetDay[];
  totals: TimesheetTotals;
  /** What the calendar asks of a full-time employee this period. */
  expected: { days: number; hours: number };
  /** `none` when the employee has not submitted this period. */
  status: TimesheetStatus | 'none';
  /** Who signed and approved it, in order. Empty until it is submitted. */
  trail: TrailEvent[];
}

export interface TimesheetInput {
  employee: Employee;
  settings: CompanySettings;
  period: { start: string; end: string };
  /** Signed sheets, so the document can state who stands behind it. */
  timesheets: Timesheet[];
  /** Hour corrections, so an edited day can be marked as such. */
  edits: EditLogEntry[];
  today: string;
}

/**
 * Build one employee's sheet.
 *
 * Pure, and reading the same engine the payslip does — a sheet and the payslip
 * that follows it can never disagree about how an hour was classified.
 */
export function buildTimesheet(input: TimesheetInput): TimesheetDoc {
  const { employee, settings, period, timesheets, edits, today } = input;
  const { start, end } = period;

  /*
   * `daysFor` addresses a period as an offset from a reference date, so the
   * reference stays `today` and the offset does the travelling. Passing the
   * period's own end *and* an offset walks back twice and returns the period
   * before the one asked for — quietly, with a full set of plausible days.
   *
   * A range the calendar does not produce falls back to being measured from its
   * own end, which is the period containing it.
   */
  const offset = offsetOf(settings, today, period);
  const reference = offset === null ? end : today;
  const at = offset ?? 0;
  const calc = calcPeriod(employee, daysFor(employee, settings, reference, 0, at), settings);

  const editedDates = new Set(
    edits.filter((e) => e.empId === employee.id && e.date >= start && e.date <= end).map((e) => e.date),
  );

  const days: TimesheetDay[] = calc.rows.map((r: DayRow) => ({
    date: r.date,
    dow: r.dow,
    weekend: r.weekend,
    holiday: r.holiday,
    hours: r.h || 0,
    dvH: r.dvH,
    yv1H: r.yv1H,
    yv2H: r.yv2H,
    gross: Math.round(r.gross),
    sick: r.sick,
    vacation: r.vacation,
    edited: editedDates.has(r.date),
    // A recorded zero is a statement that nothing was worked; no record at all
    // is the absence of a statement. The sheet distinguishes them.
    empty: !r.sick && !r.vacation && !(r.h > 0) && !(employee.worklog[r.date] !== undefined),
  }));

  const totals: TimesheetTotals = {
    dvH: calc.hours.dvH,
    yv1H: calc.hours.yv1H,
    yv2H: calc.hours.yv2H,
    hours: calc.hours.dvH + calc.hours.yv1H + calc.hours.yv2H,
    daysRecorded: days.filter((d) => !d.empty).length,
    daysWorked: calc.worked,
    sickDays: days.filter((d) => d.sick).length,
    vacationDays: days.filter((d) => d.vacation).length,
    gross: Math.round(calc.gross),
  };

  const signed = timesheets.find(
    (t) => t.empId === employee.id && t.periodKey === `${start}_${end}` && t.status !== 'rejected',
  ) ?? timesheets.find((t) => t.empId === employee.id && t.periodKey === `${start}_${end}`);

  return {
    empId: employee.id,
    name: employee.name,
    kennitala: employee.kennitala,
    jobTitle: employee.role,
    periodStart: start,
    periodEnd: end,
    days,
    totals,
    expected: expectedWork(periodInfo(settings, reference, at), settings, employee.expectedHoursOverride),
    status: signed?.status ?? 'none',
    trail: signed?.trail ?? [],
  };
}

/**
 * Which period offset the requested range is, or null if the pay calendar does
 * not produce it.
 *
 * Both ends have to match, for the same reason `buildPayrollRun` insists on it:
 * a range that merely finishes on the same day as a calendar period is not that
 * period, and answering with it would be a wrong answer that looks right.
 */
function offsetOf(
  settings: CompanySettings,
  today: string,
  period: { start: string; end: string },
): number | null {
  const matches = (offset: number): boolean => {
    const p = periodInfo(settings, today, offset);
    return p.start === period.start && p.end === period.end;
  };
  for (let offset = 0; offset >= -60; offset--) if (matches(offset)) return offset;
  for (let offset = 1; offset <= 12; offset++) if (matches(offset)) return offset;
  return null;
}

/** Everyone's sheet for one period, for the roster view and the batch export. */
export function buildTimesheets(
  employees: Employee[],
  rest: Omit<TimesheetInput, 'employee'>,
): TimesheetDoc[] {
  return employees
    .map((employee) => buildTimesheet({ ...rest, employee }))
    // Somebody with no record at all in the period has no sheet to show.
    .filter((d) => d.totals.daysRecorded > 0)
    .sort((a, b) => a.name.localeCompare(b.name, 'is'));
}
